import { Controller } from '@hotwired/stimulus';

/**
 * Advance Warning Controller
 *
 * Pilote la section « acompte » du formulaire de situation :
 * 1. Affiche un avertissement si un acompte existe déjà
 * 2. Masque le sélecteur de mode et le champ de déduction selon le type et le
 *    mode sélectionnés
 * 3. Auto-calcule la déduction d'acompte au prorata — SAUF si l'architecte a
 *    délibérément saisi un montant (le champ lui appartient alors).
 *
 * Propriété du champ de déduction (« user-owned »)
 * ------------------------------------------------
 * Deux chemins recalculent la déduction : `calculateDeduction` (saisie du
 * montant HT, mono-facture) et `onTotalChanged` (total de la table des
 * factures, multi-factures). Les DEUX doivent renoncer à écrire dès que
 * l'humain possède le champ, sinon une saisie manuelle est silencieusement
 * écrasée — au chargement de la page comme à la moindre édition de ligne.
 * La propriété est :
 *   - semée au `connect()` depuis le flag persisté `manualFlag` ('1' = manuel) ;
 *   - prise dès que l'utilisateur édite `deductionInput` (`onDeductionEdited`) ;
 *   - prise par « Tout déduire » (`deductAll`), qui écrit le RESTE de l'acompte ;
 *   - RENDUE quand l'utilisateur vide le champ (revert-to-auto implicite ;
 *     côté serveur, un champ soumis vide repasse `manual` à false et re-dérive)
 *     ou clique « Revenir au calcul automatique » (`backToAuto`).
 *
 * Aides d'état et boutons (`#refreshHints`)
 * -----------------------------------------
 * Tous les textes sont rendus par le serveur (aucune copie française ici) ; ce
 * contrôleur ne fait que basculer l'ATTRIBUT `hidden` (jamais la classe : la
 * racine de `Ui::ButtonComponent` porte `inline-flex`, qui bat `.hidden`). On
 * masque l'enveloppe nue du bouton, jamais le bouton. Trois états :
 *   - non manuel → `hintAuto` ;
 *   - manuel ET valeur = reste au centime → `hintAll` (`/edit` rend « 5000.0 »,
 *     ce contrôleur écrit « 5000.00 » : comparer en chaîne réafficherait
 *     « Tout déduire » sur une situation déjà tout déduite) ;
 *   - manuel sinon → `hintManual`.
 * « Tout déduire » est masqué quand la valeur est déjà le reste (ou qu'il ne
 * reste rien), « Revenir » tant que la saisie n'est pas manuelle.
 * Notes « Cumul précédent » : mode `null` (sélecteur absent : marché `legacy`,
 * acompte persisté) → laissées telles que le serveur les a rendues ; sinon la
 * note prorata seulement pour type « Situation de travaux » ET mode prorata.
 *
 * Targets:
 * - warning: bloc d'avertissement « un acompte existe déjà »
 * - typeSelect: select du type de situation (advance / progress)
 * - modeSelector: conteneur du sélecteur de mode de déduction
 * - modeInput: radios (mode éditable) ou hidden (mode verrouillé S2+)
 * - deductionField: conteneur du champ de déduction (masqué/désactivé en bloc)
 * - htInput: champ « montant HT de la période », TOUJOURS dans le DOM (éditable
 *   en montant unique, lecture seule et rempli par `wps-form` en plusieurs
 *   factures)
 * - deductionInput: champ « déduction acompte TTC »
 * - manualFlag: hidden `advance_deduction_manual` ('1'/'0') — persiste la
 *   propriété du champ. OPTIONNEL : le contrôleur se dégrade proprement en
 *   auto-only s'il est absent.
 * - hintAuto / hintAll / hintManual: aides d'état sous le champ de déduction
 *   (OPTIONNELLES, absentes quand il ne reste rien à déduire)
 * - deductAllWrap / backToAutoWrap: enveloppes nues des boutons « Tout déduire »
 *   et « Revenir au calcul automatique » (OPTIONNELLES, idem)
 * - cumulHintFull / cumulHintProrata: les deux notes « Cumul précédent » sous le
 *   Montant HT (OPTIONNELLES : absentes sans acompte, ou quand le champ Montant
 *   HT est en erreur)
 *
 * Values:
 * - hasExistingAdvance: un acompte existe déjà sur le marché
 * - totalAdvanceTtc / contractTotalHt / previousProgressPct / maxDeduction:
 *   entrées mode-INDÉPENDANTES du calcul prorata (colonnes persistées)
 * - previousCumulHtProrata / previousCumulHtOther: la MÊME règle serveur tranchée
 *   pour les deux modes (`compute_previous_cumulative_ht(prorata:)`). Lire via
 *   `#previousCumulHt()`, jamais en direct — voir la section « Mode de déduction »
 * - minDeduction: PLANCHER de la déduction, ≤ 0, rendu par le serveur
 *   (= −(cumul d'acompte déjà déduit avant cette situation), la même valeur que
 *   le `min` du champ). Absente, elle vaut 0 : le plancher d'avant.
 *
 * Plancher négatif (situation négative, P4 / D6)
 * ----------------------------------------------
 * La déduction au prorata SUIT l'avancement : quand un avoir le fait reculer,
 * elle devient négative (l'acompte est recrédité), jamais au-delà de ce qui a
 * déjà été déduit. Le serveur écrête au même plancher (auto et saisie manuelle,
 * `advance_deduction_calculations.rb`) ; ce contrôleur ne fait que LIRE la
 * borne rendue, il ne la recalcule pas. Un plancher à 0 ici écrivait « 0.00 »
 * dans le champ, et le serveur gardait ce 0 (toute saisie non vide est prise).
 * « Tout déduire » n'est pas concerné : il écrit le reste, toujours ≥ 0.
 *
 * Valeur pré-remplie par le serveur (« ne pas écraser au chargement »)
 * ------------------------------------------------------------------
 * La propriété ci-dessus ne protège QU'UNE saisie manuelle. Une déduction AUTO
 * (`advance_deduction_manual = false`) est, elle, rendue par le serveur dans le
 * champ à l'ouverture d'`/edit` — et doit survivre au chargement de la page.
 * `invoice-lines-table` dispatche son total depuis son PROPRE `connect()` : sur
 * une situation mono-facture ce total vaut 0, donc `onTotalChanged` réécrivait
 * « 7 200,00 » en « 0.00 » avant tout geste. À l'envoi, ce 0 explicite est
 * indiscernable d'un 0 voulu côté serveur → l'acompte n'est jamais récupéré.
 *
 * L'invariant, UNIQUE et partagé par les DEUX chemins de recalcul :
 *   **une passe programmatique (connexion) n'écrase jamais une valeur rendue
 *   par le serveur.**
 * Il se décide sur deux entrées locales, sans aucun état partagé :
 *   - `initial` — passe de connexion (`connect()` pour `calculateDeduction`,
 *     `detail.initial` pour `onTotalChanged`) vs geste utilisateur ;
 *   - `deductionInput.defaultValue` — l'attribut HTML `value`, insensible à nos
 *     propres écritures `.value =`, donc « le serveur a-t-il rempli ce champ ? »
 * JAMAIS « le champ est-il pré-rempli ? » seul : un champ pré-rempli DOIT se
 * recalculer sur une vraie édition (ligne de facture, montant HT, ou retour au
 * mode prorata).
 *
 * Il n'y a volontairement PLUS de drapeau `_htInputChanged` : c'était un proxy
 * de « on n'est plus à la connexion », armé seulement si l'exécution atteignait
 * sa ligne — donc désarmé par toute sortie anticipée placée au-dessus (mode ≠
 * prorata, contractTotalHt = 0), ce qui gelait `calculateDeduction` pour de bon
 * (WPS-PRO-09). Ne pas le réintroduire : chaque méthode juge sur ses propres
 * entrées.
 *
 * Mode de déduction : une base par mode, choisie ici (JAMAIS recalculée)
 * ---------------------------------------------------------------------
 * Le mode est choisi côté CLIENT, long après le rendu de la page. La SEULE
 * entrée du calcul qui dépende du mode est le cumul précédent : le serveur
 * exclut l'acompte de la chaîne HT en mode prorata seulement
 * (`should_exclude_advance_from_ht_chain?`). Une base unique cuite pour le mode
 * RENDU est donc périmée dès que le mode sélectionné en diffère.
 *
 * Le serveur tranche donc la règle DANS LES DEUX SENS et rend DEUX valeurs
 * (`previousCumulHtProrata` / `previousCumulHtOther`) ; ce contrôleur ne fait que
 * CHOISIR entre elles (`#previousCumulHt()`). Il ne réimplémente JAMAIS
 * `should_exclude_advance_from_ht_chain?` : ce qui est incertain au rendu, c'est
 * le MODE, pas la règle — dupliquer la règle en JS la ferait diverger au premier
 * changement métier. Toutes les autres entrées (`previousProgressPct`,
 * `maxDeduction`, `totalAdvanceTtc`, `contractTotalHt`) lisent des colonnes
 * persistées : elles sont mode-indépendantes.
 *
 * `currentPct` DOIT dériver de la base ainsi choisie : côté serveur
 * `progress_percentage` est lui-même calculé depuis `compute_previous_cumulative_ht`
 * (`calculate_progress_from_amount`, un `before_validation`), puis relu par
 * `auto_advance_deduction_ttc` — mode → cumul → avancement → déduction est une
 * boucle. Client et serveur s'accordent par construction : le contrôleur écrit le
 * mode du marché (`set_advance_deduction_mode`) AVANT de sauvegarder la situation,
 * donc le serveur re-dérive contre le mode FRAIS.
 *
 * Il n'y a volontairement PLUS de garde « mode périmé » qui vidait le champ pour
 * rendre la main au serveur : la base n'est plus périmée, donc il n'y a plus rien
 * à supprimer. Cette garde comparait le mode sélectionné à `radio.defaultChecked`
 * ; or `_advance_mode_selector` coche `full` quand le mode vaut `nil` (le défaut
 * en prod), donc sur `/new` le tout PREMIER choix « prorata » — pas une bascule —
 * passait pour une bascule et supprimait l'aperçu à jamais. Ne pas la
 * réintroduire : la bonne réponse à une base périmée est de ne pas avoir de base
 * périmée.
 *
 * Actions:
 * - syncVisibility: sur changement de type ou de mode (+ appelée par `connect`)
 * - calculateDeduction: sur saisie du montant HT (mono-facture)
 * - onTotalChanged: sur `invoice-lines:total-changed@window` (multi-factures)
 * - onDeductionEdited: sur ÉDITION HUMAINE de `deductionInput` — prend ou rend
 *   la propriété du champ. Ne jamais déclencher depuis du code : les écritures
 *   programmatiques (`#applyDeduction`) passent par `.value =`, qui n'émet
 *   aucun événement `input`. Émettre un `input` synthétique ici verrouillerait
 *   le flag à '1' pour toujours et transformerait l'auto-calcul en code mort.
 * - deductAll: « Tout déduire sur cette situation » — écrit `maxDeduction`
 *   (le reste), prend la propriété du champ, focus sur « Revenir ».
 * - backToAuto: « Revenir au calcul automatique » — vide le champ, rend la
 *   propriété, recalcule ; focus sur « Tout déduire » s'il est visible, sinon
 *   sur le champ de déduction.
 *
 * @example
 *   <form data-controller="advance-warning"
 *         data-action="invoice-lines:total-changed@window->advance-warning#onTotalChanged">
 *     <p>Cumul précédent :
 *       <span data-advance-warning-target="cumulHintFull">… (acompte compris)</span>
 *       <span data-advance-warning-target="cumulHintProrata" hidden>… (acompte non compris, …)</span>
 *     </p>
 *     <input data-advance-warning-target="deductionInput"
 *            data-action="input->advance-warning#onDeductionEdited">
 *     <input type="hidden" name="work_progress_statement[advance_deduction_manual]"
 *            value="0" data-advance-warning-target="manualFlag">
 *     <p aria-live="polite" aria-atomic="true">
 *       <span data-advance-warning-target="hintAuto">Calcul automatique : …</span>
 *       <span data-advance-warning-target="hintAll" hidden>Tout le reste …</span>
 *       <span data-advance-warning-target="hintManual" hidden>Montant saisi à la main …</span>
 *     </p>
 *     <span data-advance-warning-target="deductAllWrap">
 *       <button type="button" data-action="advance-warning#deductAll">Tout déduire …</button>
 *     </span>
 *     <span data-advance-warning-target="backToAutoWrap" hidden>
 *       <button type="button" data-action="advance-warning#backToAuto">Revenir …</button>
 *     </span>
 *   </form>
 */
export default class extends Controller {
  static targets = [
    'warning',
    'typeSelect',
    'modeSelector',
    'deductionField',
    'modeInput',
    'htInput',
    'deductionInput',
    'manualFlag',
    'hintAuto',
    'hintAll',
    'hintManual',
    'deductAllWrap',
    'backToAutoWrap',
    'cumulHintFull',
    'cumulHintProrata',
  ];
  static values = {
    hasExistingAdvance: Boolean,
    totalAdvanceTtc: Number,
    contractTotalHt: Number,
    previousCumulHtProrata: Number,
    previousCumulHtOther: Number,
    previousProgressPct: Number,
    maxDeduction: Number,
    minDeduction: Number,
  };

  connect() {
    // Sème la propriété AVANT toute passe de recalcul. `invoice-lines-table`
    // dispatche son total dans son propre `connect()`, donc `onTotalChanged`
    // peut tirer immédiatement après celui-ci : la graine doit être en place.
    this._userOwned = this.#manualFlagValue();
    // `initial: true` — cette passe précède tout geste utilisateur.
    this.syncVisibility({ initial: true });
  }

  // Called on type change and mode change (data-action) AND from `connect()`.
  //
  // `arg` is an Event when Stimulus invokes it from a `data-action` (a real
  // user gesture — an Event carries no `initial`, so the flag reads false), and
  // `{ initial: true }` when `connect()` calls it. That is the ONLY distinction
  // that matters downstream: see `calculateDeduction`.
  syncVisibility(arg) {
    const initial = arg?.initial === true;
    const selectedType = this.typeSelectTarget.value;
    const isAdvanceSelected = selectedType === 'advance';
    const isProgressSelected = selectedType === 'progress';

    // Warning: existing advance message
    if (this.hasWarningTarget) {
      this.warningTarget.classList.toggle(
        'hidden',
        !(isAdvanceSelected && this.hasExistingAdvanceValue)
      );
    }

    // Mode selector: visible only for progress type, disabled to prevent stale submission
    if (this.hasModeSelectorTarget) {
      this.modeSelectorTarget.classList.toggle('hidden', !isProgressSelected);
      this.modeInputTargets.forEach((input) => {
        if (input.type === 'radio') input.disabled = !isProgressSelected;
      });
    }

    // Deduction field: visible only when type=progress AND mode=prorata
    if (this.hasDeductionFieldTarget) {
      const selectedMode = this.#selectedMode();
      const showDeduction = isProgressSelected && selectedMode === 'prorata';
      this.deductionFieldTarget.classList.toggle('hidden', !showDeduction);
      this.deductionFieldTarget.querySelectorAll('input, select').forEach((input) => {
        input.disabled = !showDeduction;
      });
    }

    // Auto-calculate deduction when field becomes visible. Propagates `initial`
    // so a connect-time visibility pass never counts as an edit.
    this.calculateDeduction({ initial });
    // After the recompute, so hints judge the final value (and the cumul note
    // follows the type / mode just chosen).
    this.#refreshHints();
  }

  // Called on HT input change (data-action) AND from `syncVisibility`.
  // Auto-fills the prorata deduction amount.
  //
  // `arg` is an Event on the `input->` path (user gesture ⇒ initial false), or
  // `{ initial }` forwarded by `syncVisibility`.
  calculateDeduction(arg) {
    const initial = arg?.initial === true;
    // The HT field is ALWAYS in the DOM: editable in single-amount mode,
    // readonly and filled by `wps-form#onTotalChanged` in multi-invoice mode
    // (where `onTotalChanged` below also drives the recompute). The guard only
    // covers a form that renders neither field.
    if (!this.hasHtInputTarget || !this.hasDeductionInputTarget) return;
    // Never overwrite a value the architect owns (typed this session, or
    // persisted as manual on a previous visit).
    if (this.#userOwnsDeduction()) return;

    const selectedMode = this.#selectedMode();
    if (selectedMode !== 'prorata') return;
    if (this.contractTotalHtValue === 0) return;

    // Same rule as `onTotalChanged`: a connect-time pass must not overwrite a
    // value the SERVER rendered. `defaultValue` is the HTML `value` attribute
    // (immune to our own `.value =` writes), so it answers « le serveur a-t-il
    // rempli ce champ ? » on its own — no cross-method flag required.
    if (initial && this.deductionInputTarget.defaultValue !== '') return;

    const periodHt = parseFloat(this.htInputTarget.value) || 0;
    this.#applyDeduction(periodHt);
  }

  // Listener for `invoice-lines:total-changed@window` — recomputes the prorata
  // deduction from the table's running totalHt (the HT field is readonly in
  // multi-invoice mode and is filled from the same event by `wps-form`, in an
  // order this controller does not control).
  //
  // `detail.initial` is TRUE only for the dispatch `invoice-lines-table` fires
  // from its own `connect()` — see that controller's docblock. It is the
  // load-bearing distinction here, and it is NOT the same question as
  // « le champ est-il pré-rempli ? » : a prefilled field must still recompute
  // on a real edit, so gating on prefill alone would trade a zeroing bug for a
  // silent stale-number bug.
  onTotalChanged({ detail: { totalHt, initial = false } = {} } = {}) {
    if (!this.hasDeductionInputTarget) return;
    // Never overwrite a value the architect owns. `invoice-lines-table`
    // dispatches this event from its own `connect()`, so without this guard a
    // manual amount is clobbered on page load, before any user gesture.
    if (this.#userOwnsDeduction()) return;

    const selectedMode = this.#selectedMode();
    if (selectedMode !== 'prorata') return;
    if (this.contractTotalHtValue === 0) return;

    // Connect-time dispatch over a value the SERVER rendered: not an edit, so
    // leave it alone. Mirror of the guard in `calculateDeduction` — each method
    // now decides on its own inputs alone, with no shared flag between them.
    if (initial && this.deductionInputTarget.defaultValue !== '') return;

    this.#applyDeduction(parseFloat(totalHt) || 0);
  }

  // Called on a GENUINE human edit of `deductionInput` (wired to `input->`).
  // Programmatic writes in `#applyDeduction` use `.value =`, which fires no
  // `input` event — so this never runs against our own output.
  onDeductionEdited() {
    if (!this.hasDeductionInputTarget) return;

    // Clearing the field is the implicit revert-to-auto: ownership goes back to
    // the calculator, which refills on the next total/HT change (and the server
    // re-derives a submitted-but-blank input).
    const cleared = this.deductionInputTarget.value.trim() === '';
    this.#setUserOwned(!cleared);
    this.#refreshHints();
  }

  // « Tout déduire sur cette situation » : writes the REST of the advance
  // (`maxDeduction`, already net of what earlier statements deducted, rendered
  // by the server) and takes ownership, so neither a later HT edit nor the
  // server's auto path re-derives it. No arithmetic beyond the floor at 0.
  deductAll() {
    if (!this.hasDeductionInputTarget) return;

    // Plain assignment on purpose (same rule as `#applyDeduction`): no `input`
    // event. Ownership is taken explicitly below instead.
    this.deductionInputTarget.value = Math.max(this.maxDeductionValue, 0).toFixed(2);
    this.#setUserOwned(true);
    this.#refreshHints();

    // WCAG 2.4.3: the clicked button just got hidden, move focus to its twin.
    this.#focusButtonIn(this.hasBackToAutoWrapTarget ? this.backToAutoWrapTarget : null);
  }

  // « Revenir au calcul automatique » : empties the field BEFORE recomputing,
  // so a recompute that cannot write (contract total 0) leaves it blank, which
  // the server re-derives, instead of a stale amount flagged auto.
  backToAuto() {
    if (!this.hasDeductionInputTarget) return;

    this.deductionInputTarget.value = '';
    this.#setUserOwned(false);
    this.calculateDeduction();
    this.#refreshHints();

    const wrap = this.hasDeductAllWrapTarget ? this.deductAllWrapTarget : null;
    if (!this.#focusButtonIn(wrap)) this.deductionInputTarget.focus();
  }

  #applyDeduction(periodHt) {
    // `currentPct` DOIT dériver de la base du mode SÉLECTIONNÉ : c'est
    // exactement ce que fait le serveur (`calculate_progress_from_amount` puis
    // `auto_advance_deduction_ttc`). Lire une autre base ici, c'est diverger du
    // serveur précisément sur le mode.
    const newCumulHt = this.#previousCumulHt() + periodHt;
    const currentPct = newCumulHt / this.contractTotalHtValue;
    const prevPct = this.previousProgressPctValue / 100;
    const autoAmount = (currentPct - prevPct) * this.totalAdvanceTtcValue;
    // Floor = the server-rendered −(already deducted), never above 0 (a stray
    // positive value must not force a deduction). Absent value ⇒ 0, as before.
    const floor = Math.min(this.minDeductionValue, 0);
    const capped = Math.min(Math.max(autoAmount, floor), Math.max(this.maxDeductionValue, 0));
    // A tiny negative auto amount rounds to « -0.00 »: write « 0.00 ».
    const text = capped.toFixed(2);

    // Plain assignment on purpose: it fires no `input` event, so it cannot trip
    // `onDeductionEdited` into flagging the field manual. Do NOT dispatch one.
    this.deductionInputTarget.value = text === '-0.00' ? '0.00' : text;
    this.#refreshHints();
  }

  // Shows the hint, buttons and cumul note matching the current state. Toggles
  // the `hidden` ATTRIBUTE only (see header), and only on a change, so the
  // `aria-live` hint is not re-announced on every keystroke.
  #refreshHints() {
    if (this.hasDeductionInputTarget) {
      const owned = this.#userOwnsDeduction();
      const isAll = owned && this.#equalsMaxDeduction(this.deductionInputTarget.value);

      if (this.hasHintAutoTarget) this.#setHidden(this.hintAutoTarget, owned);
      if (this.hasHintAllTarget) this.#setHidden(this.hintAllTarget, !isAll);
      if (this.hasHintManualTarget) this.#setHidden(this.hintManualTarget, !owned || isAll);

      if (this.hasDeductAllWrapTarget) {
        const nothingLeft = !(this.maxDeductionValue > 0);
        this.#setHidden(
          this.deductAllWrapTarget,
          nothingLeft || this.#equalsMaxDeduction(this.deductionInputTarget.value)
        );
      }
      if (this.hasBackToAutoWrapTarget) this.#setHidden(this.backToAutoWrapTarget, !owned);
    }

    this.#refreshCumulHints();
  }

  // « Cumul précédent » note under the HT field. Mode `null` = no selector on
  // the page (legacy contract, persisted advance): the client cannot tell which
  // chain the server used, so the server-rendered state stands.
  #refreshCumulHints() {
    const mode = this.#selectedMode();
    if (mode === null || !this.hasTypeSelectTarget) return;

    const prorata = this.typeSelectTarget.value === 'progress' && mode === 'prorata';
    if (this.hasCumulHintFullTarget) this.#setHidden(this.cumulHintFullTarget, prorata);
    if (this.hasCumulHintProrataTarget) this.#setHidden(this.cumulHintProrataTarget, !prorata);
  }

  // Equality with the rest AT THE CENT: `/edit` renders « 5000.0 », this
  // controller writes « 5000.00 ».
  #equalsMaxDeduction(rawValue) {
    const amount = parseFloat(rawValue);
    if (!Number.isFinite(amount)) return false;
    if (!(this.maxDeductionValue > 0)) return false;
    return Math.round(amount * 100) === Math.round(this.maxDeductionValue * 100);
  }

  #setHidden(element, hidden) {
    if (element.hidden !== hidden) element.hidden = hidden;
  }

  // Focuses the button inside `wrap` when the wrap is visible. Returns whether
  // it did, so the caller can fall back to another target.
  #focusButtonIn(wrap) {
    if (!wrap || wrap.hidden) return false;
    const button = wrap.querySelector('button');
    if (!button) return false;
    button.focus();
    return true;
  }

  // True when the human owns the deduction amount and the calculator must not
  // write to it.
  #userOwnsDeduction() {
    // Defensive: `connect()` always seeds this before a recompute path can
    // fire, but a missing seed must never license an overwrite.
    if (this._userOwned === undefined) this._userOwned = this.#manualFlagValue();
    return this._userOwned === true;
  }

  #setUserOwned(owned) {
    this._userOwned = owned;
    if (this.hasManualFlagTarget) this.manualFlagTarget.value = owned ? '1' : '0';
  }

  // Reads the persisted `advance_deduction_manual` flag. Degrades to `false`
  // (auto-only, pre-flag behaviour) when the hidden input isn't rendered.
  #manualFlagValue() {
    return this.hasManualFlagTarget && this.manualFlagTarget.value === '1';
  }

  // Le cumul précédent de la chaîne HT POUR LE MODE SÉLECTIONNÉ.
  //
  // Le serveur a tranché `should_exclude_advance_from_ht_chain?` dans les deux
  // sens (`compute_previous_cumulative_ht(prorata: true/false)`) ; on ne fait que
  // CHOISIR. Ne JAMAIS réimplémenter la règle ici : elle a d'autres entrées
  // (présence d'un acompte, identité/type de la situation précédente) que le
  // client ne connaît pas, et la dupliquer la ferait diverger en silence.
  //
  // `previousCumulHtOther` couvre tous les modes non-prorata (`full`, `nil`,
  // `legacy`) : c'est la chaîne brute, sans exclusion.
  #previousCumulHt() {
    return this.#selectedMode() === 'prorata'
      ? this.previousCumulHtProrataValue
      : this.previousCumulHtOtherValue;
  }

  // Returns the currently selected advance deduction mode.
  // Works with both radio inputs (editable mode) and hidden inputs (locked mode on S2+).
  #selectedMode() {
    // Try checked radio first (editable mode)
    const checkedRadio = this.modeInputTargets.find(
      (input) => input.type === 'radio' && input.checked
    );
    if (checkedRadio) return checkedRadio.value;

    // Fallback to hidden input (locked mode on S2+)
    const hiddenInput = this.modeInputTargets.find((input) => input.type === 'hidden');
    return hiddenInput?.value || null;
  }
}
